import { useLocation, useHistory } from 'react-router-dom';
import { useMemo, useCallback } from 'react';
import {
  extractExternalReference,
  clearExternalReferenceFromCurrentUrl,
  clearExternalReferenceFromStorage,
  clearNameIdFromCurrentUrl,
  clearNameIdFromStorage,
  clearSourceFromCurrentUrl,
  clearSourceFromStorage
} from '../utils';
import { ExternalReference } from '../types';

/**
 * Hook for handling external reference authentication (nameId & source parameters)
 *
 * This hook reads parameters from BOTH current URL and stored redirect URL,
 * External references can be passed via deep links to any page in the application.
 *
 * @returns External reference data and utilities
 */
export function useExternalReference() {
  const location = useLocation();
  const history = useHistory();

  const externalReference = useMemo((): ExternalReference | null => {
    return extractExternalReference(location.search);
  }, [location.search]);

  const clearExternalReferenceParams = useCallback(() => {
    clearExternalReferenceFromCurrentUrl(location, history);
    clearExternalReferenceFromStorage();
  }, [location, history]);

  const clearNameIdParams = useCallback(() => {
    clearNameIdFromCurrentUrl(location, history);
    clearNameIdFromStorage();
  }, [location, history]);

  const clearSourceParams = useCallback(() => {
    clearSourceFromCurrentUrl(location, history);
    clearSourceFromStorage();
  }, [location, history]);

  const hasExternalReference = !!(
    externalReference?.nameId || externalReference?.source
  );

  return {
    externalReference,
    hasExternalReference,
    clearExternalReferenceParams,
    clearNameIdParams,
    clearSourceParams
  };
}
